import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Spinner } from '../../../shared/components/ui';
import { InsightsFilterBar } from './InsightsFilterBar';
import { KPICard } from './KPICard';
import { CostTrendChart } from './CostTrendChart';
import { CategoryBreakdownChart } from './CategoryBreakdownChart';
import { JobTypeDistributionChart } from './JobTypeDistributionChart';
import { GeographyChart } from './GeographyChart';
import { FeeTrendChart } from './FeeTrendChart';
import { AvgFeeAmountTrendChart } from './AvgFeeAmountTrendChart';
import {
  fetchAllEstimates,
  buildApiFilters,
  applyClientFilters,
  extractUniqueLocations,
  extractUniqueJobTypes,
  extractUniqueCategories,
  computeKPIs,
  DEFAULT_FILTERS,
} from '../../../shared/services/insightsApi';
import {
  formatCurrencyCompact,
  formatNumber,
} from '../../../shared/utils/formatters';

// ─────────────────────────────────────────────────────────────────────────────
// KPI icons (inline SVGs, same style as rest of the app)
// ─────────────────────────────────────────────────────────────────────────────

function IconProjects() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function IconCostPerSf() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function IconPortfolio() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export function Insights() {
  const navigate = useNavigate();

  // Filter state
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });

  // Data state
  const [allEstimates, setAllEstimates] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [availableJobTypes, setAvailableJobTypes] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Effect 1: Re-fetch when server-side filter (location) changes ──────────
  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setIsLoading(true);
      setError(null);
      try {
        const apiFilters = buildApiFilters(filters);
        const { estimates, jobTypes } = await fetchAllEstimates(apiFilters);

        if (cancelled) return;

        setAllEstimates(estimates);

        // Derive available filter options from the full unfiltered dataset
        setAvailableLocations(extractUniqueLocations(estimates));

        // Use job types from API response if available, otherwise derive from data
        const types =
          jobTypes && jobTypes.length > 0 ? jobTypes : extractUniqueJobTypes(estimates);
        setAvailableJobTypes(types);
        setAvailableCategories(extractUniqueCategories(estimates));
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load insights data:', err);
          setError('Failed to load estimates. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
    // Only re-fetch when the API-level filter changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.location]);

  // ── Derived: apply client-side filters whenever allEstimates or filters change
  const filteredEstimates = useMemo(
    () => applyClientFilters(allEstimates, filters),
    [allEstimates, filters]
  );

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => computeKPIs(filteredEstimates), [filteredEstimates]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleFiltersChange = useCallback((updatedFilters) => {
    setFilters(updatedFilters);
  }, []);

  const handleRetry = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS });
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 -mx-4 sm:-mx-6 lg:-mx-8 -mt-6">
      {/* ── Dark analytics header ────────────────────────────────────────── */}
      <div className="bg-slate-900 px-4 sm:px-6 lg:px-8 pt-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-white">Insights</h1>
            <Badge variant="purple" size="lg">Pro</Badge>
          </div>
          <p className="text-slate-400 text-sm">
            Portfolio analytics
            {!isLoading && allEstimates.length > 0 && (
              <> across <span className="text-slate-300 font-medium">{formatNumber(allEstimates.length)}</span> estimates</>
            )}
          </p>
        </div>
      </div>

      {/* ── KPI cards overlapping dark header ───────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-14 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KPICard
            title="Total Projects"
            value={formatNumber(kpis.totalProjects)}
            subtitle={isLoading ? 'Loading…' : 'in selected period'}
            icon={<IconProjects />}
            accentColor="blue"
          />
          <KPICard
            title="Avg Cost / SF"
            value={
              kpis.avgCostPerSf != null
                ? `$${kpis.avgCostPerSf.toFixed(2)}`
                : '—'
            }
            subtitle="average estimated cost per SF"
            icon={<IconCostPerSf />}
            accentColor="emerald"
          />
          <KPICard
            title="Portfolio Value"
            value={formatCurrencyCompact(kpis.totalValue) || '—'}
            subtitle="total estimated cost"
            icon={<IconPortfolio />}
            accentColor="slate"
          />
        </div>
      </div>

      {/* ── Filter bar ────────────────────────────────────────────────────── */}
      <div className="mt-6">
        <InsightsFilterBar
          filters={filters}
          onChange={handleFiltersChange}
          availableJobTypes={availableJobTypes}
          availableLocations={availableLocations}
          availableCategories={availableCategories}
          isLoading={isLoading}
        />
      </div>

      {/* ── Content area ─────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Spinner size="lg" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading analytics…</p>
          </div>
        )}

        {/* Error */}
        {!isLoading && error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl p-8 text-center">
            <svg className="w-10 h-10 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="text-base font-semibold text-red-800 dark:text-red-300 mb-2">Failed to load data</h3>
            <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              onClick={handleRetry}
              className="px-4 py-2 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && allEstimates.length === 0 && (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-12 text-center shadow-sm">
            <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No estimates yet</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Upload your historical estimates to start seeing insights about your cost trends,
              geographic distribution, and budget performance.
            </p>
            <button
              onClick={() => navigate('/estimates/upload')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Upload Estimates
            </button>
          </div>
        )}

        {/* Empty filtered state (data exists but current filters return nothing) */}
        {!isLoading && !error && allEstimates.length > 0 && filteredEstimates.length === 0 && (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-10 text-center shadow-sm">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-1">No results for current filters</h3>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Try adjusting the time period, location, or job type filters
            </p>
          </div>
        )}

        {/* Charts — only render when we have data */}
        {!isLoading && !error && filteredEstimates.length > 0 && (
          <>
            {/* Row 1: Cost Trend (full width) */}
            <CostTrendChart estimates={filteredEstimates} />

            {/* Row 2: Category + Job Type (side by side) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CategoryBreakdownChart estimates={filteredEstimates} />
              <JobTypeDistributionChart estimates={filteredEstimates} />
            </div>

            {/* Row 3: Geography + Fee Trend (side by side) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <GeographyChart estimates={filteredEstimates} />
              <FeeTrendChart estimates={filteredEstimates} />
            </div>

            {/* Row 4: Avg Fee Amount Trend (full width) */}
            <AvgFeeAmountTrendChart estimates={filteredEstimates} />

            {/* Row 5: Summary stats */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-6">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">Summary Statistics</h3>
              <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium mb-1">Total Estimates</dt>
                  <dd className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatNumber(filteredEstimates.length)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium mb-1">Completed</dt>
                  <dd className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {formatNumber(filteredEstimates.filter((e) => e.projectCompleted).length)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium mb-1">Prevailing Wage</dt>
                  <dd className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {formatNumber(filteredEstimates.filter((e) => e.prevailingWage).length)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium mb-1">Portfolio Value</dt>
                  <dd className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatCurrencyCompact(kpis.totalValue)}</dd>
                </div>
                {kpis.avgCostPerSf != null && (
                  <div>
                    <dt className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium mb-1">Avg Cost / SF</dt>
                    <dd className="text-xl font-bold text-gray-900 dark:text-gray-100">${kpis.avgCostPerSf.toFixed(2)}</dd>
                  </div>
                )}
              </dl>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
                Data reflects {filteredEstimates.length} of {allEstimates.length} total estimates
                {filters.location && ` in ${filters.location}`}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Insights;
